import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { 
    Camera, X, ChevronLeft, ChevronRight, Shield, Building2, Home, Star
} from 'lucide-react';

const categories = [
    { id: 'all', label: 'All Photos' },
    { id: 'armed', label: 'Armed Security' },
    { id: 'corporate', label: 'Corporate' },
    { id: 'residential', label: 'Residential' },
    { id: 'events', label: 'Events' },
];

const galleryImages = [
    { src: '/images/f1.jpg', alt: 'Security team briefing', category: 'armed', icon: Shield, title: 'Armed Guards', description: 'Professional armed security personnel on duty' },
    { src: '/gallery/g4.jpg', alt: 'Security team lineup', category: 'armed', icon: Shield, title: 'Armed Protection', description: 'Highly trained armed security team' },
    { src: '/gallery/g1.jpg', alt: 'Lady security guards', category: 'armed', icon: Shield, title: 'Lady Guards', description: 'Trained female security personnel on duty' },
    { src: '/gallery/g3.jpg', alt: 'Security staff assembly', category: 'corporate', icon: Building2, title: 'Office Security', description: 'Corporate office security management' },
    { src: '/gallery/sss4.jpeg', alt: 'On-site security briefing', category: 'corporate', icon: Building2, title: 'Corporate Security', description: 'Comprehensive corporate protection' },
    { src: '/gallery/sss5.jpeg', alt: 'Field security operations', category: 'corporate', icon: Building2, title: 'IT Park Security', description: 'Tech park security operations' },
    { src: '/gallery/sss6.jpeg', alt: 'Team meeting', category: 'corporate', icon: Building2, title: 'Business Security', description: 'Business establishment protection' },
    { src: '/images/f12.jpg', alt: 'Housekeeping staff', category: 'corporate', icon: Building2, title: 'Housekeeping Staffs', description: 'Professional housekeeping staff for offices and facilities' },
    { src: '/gallery/sss7.jpeg', alt: 'Residential security patrol', category: 'residential', icon: Home, title: 'Residential Security', description: 'Residential area security services' },
    { src: '/images/f9.jpg', alt: 'Residential security team', category: 'residential', icon: Home, title: 'Apartment Security', description: 'Apartment and gated community protection' },
    { src: '/gallery/sss8.jpeg', alt: 'Office staff', category: 'events', icon: Star, title: 'Office Staff', description: 'Professional office staff support' },
];

export const GalleryPage = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const filteredImages = activeCategory === 'all' 
        ? galleryImages 
        : galleryImages.filter(img => img.category === activeCategory);

    const openLightbox = (image, index) => {
        setCurrentImage(image);
        setCurrentIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setCurrentImage(null);
    };

    const nextImage = () => {
        const newIndex = (currentIndex + 1) % filteredImages.length;
        setCurrentIndex(newIndex);
        setCurrentImage(filteredImages[newIndex]);
    };

    const prevImage = () => {
        const newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
        setCurrentIndex(newIndex);
        setCurrentImage(filteredImages[newIndex]);
    };

    const getCategoryLabel = (categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        return cat ? cat.label : categoryId;
    };

    return (
        <div className="overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-[50vh] lg:min-h-[60vh] flex items-center pt-24 pb-12">
                {/* Background */}
                <div className="absolute inset-0 bg-muted/50" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
                {/* Watermark Image - Hero Only */}
                <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ 
                        backgroundImage: 'url(/images/gallary.jpg)',
                        filter: 'saturate(0.3) brightness(1.1)',
                        opacity: '0.1'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
                
                <div className="container mx-auto px-4 lg:px-8 relative z-10">
                    <div className="max-w-3xl">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-6 animate-fade-in">
                            <Camera className="w-3 h-3 mr-1" />
                            GALLERY
                        </Badge>
                        <h1 className="font-display text-5xl lg:text-7xl text-foreground leading-tight mb-6 animate-fade-in animation-delay-100">
                            OUR WORK IN
                            <span className="text-accent block">ACTION</span>
                        </h1>
                        <p className="text-muted-foreground text-lg lg:text-xl leading-relaxed animate-fade-in animation-delay-200">
                            Explore our professional security operations across various sectors. 
                            From corporate environments to event security, see our team in action.
                        </p>
                    </div>
                </div>
                
                {/* Horizontal Divider */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            </section>

            {/* Gallery Section with Filter */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    {/* Category Filter Tabs */}
                    <div className="flex flex-wrap justify-center gap-3 mb-12">
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setActiveCategory(cat.id)}
                                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
                                    activeCategory === cat.id
                                        ? 'bg-accent text-accent-foreground shadow-lg'
                                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {/* Image Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredImages.map((image, index) => (
                            <Card 
                                key={index}
                                className="card-elevated overflow-hidden cursor-pointer group hover:border-accent/30 transition-all duration-300"
                                onClick={() => openLightbox(image, index)}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img 
                                        src={image.src}
                                        alt={image.alt}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    {/* Category Badge */}
                                    <Badge className="absolute top-3 right-3 bg-card/90 backdrop-blur-sm text-foreground text-xs">
                                        {getCategoryLabel(image.category)}
                                    </Badge>
                                    {/* Icon */}
                                    <div className="absolute bottom-3 left-3 w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                                        <image.icon className="w-5 h-5 text-accent-foreground" />
                                    </div>
                                </div>
                                <div className="p-4">
                                    <h3 className="font-display text-lg text-foreground mb-1">{image.title}</h3>
                                    <p className="text-muted-foreground text-sm">{image.description}</p>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox Modal */}
            {lightboxOpen && currentImage && (
                <div 
                    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
                    onClick={closeLightbox}
                >
                    <button 
                        className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
                        onClick={closeLightbox}
                    >
                        <X className="w-8 h-8" />
                    </button>
                    
                    <button 
                        className="absolute left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    >
                        <ChevronLeft className="w-10 h-10" />
                    </button>
                    
                    <img 
                        src={currentImage.src}
                        alt={currentImage.alt}
                        decoding="async"
                        className="max-w-[90vw] max-h-[85vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                    
                    <button 
                        className="absolute right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    >
                        <ChevronRight className="w-10 h-10" />
                    </button>
                    
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
                        {currentIndex + 1} / {filteredImages.length}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryPage;
